import { useMutation } from "react-query";
import axios from "axios";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import {
  DocumentCheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useAppContext } from "../AppProviders";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import {
  fetchLegacy360ConsentFormPresignedUrl,
  fetchLegacy360ReportPdfPresignedUrl,
} from "../api/Legacy360Api";
import { downloadLegacy360PresignedFile } from "./legacy360PresignedDownload";

function InlineSpinner() {
  return (
    <span
      className="inline-block w-5 h-5 border-2 border-gray-200 border-t-current rounded-full animate-spin"
      aria-hidden
    />
  );
}

export default function Legacy360DownloadActions({
  reportUuid,
}: {
  reportUuid: string;
}) {
  const { requestApi } = useAppContext();
  const { showLargeToast } = useApplicationToast();

  const toastError = (message: string) =>
    showLargeToast?.({
      title: "Error!",
      message,
      icon: (
        <div className="bg-red-100 text-red-600 rounded-full p-2">
          <ExclamationTriangleIcon className="w-6 h-6" />
        </div>
      ),
    });

  const toastConsentNotFound = () =>
    showLargeToast?.({
      title: "Consent form not found",
      message: "",
      icon: (
        <div className="bg-amber-100 text-amber-700 rounded-full p-2">
          <ExclamationTriangleIcon className="w-6 h-6" />
        </div>
      ),
    });

  const pdfMutation = useMutation(
    () => fetchLegacy360ReportPdfPresignedUrl(requestApi, reportUuid),
    {
      onSuccess: ({ report_url: url, filename }) => {
        downloadLegacy360PresignedFile(url, filename);
      },
      onError: () =>
        toastError(
          "Could not download report PDF. Try again later or contact support."
        ),
    }
  );

  const consentMutation = useMutation(
    () => fetchLegacy360ConsentFormPresignedUrl(requestApi, reportUuid),
    {
      onSuccess: ({ report_url: url, filename }) => {
        downloadLegacy360PresignedFile(url, filename);
      },
      onError: (err) => {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          toastConsentNotFound();
          return;
        }
        toastError(
          "Could not download consent form. Try again later or contact support."
        );
      },
    }
  );

  const busy = pdfMutation.isLoading || consentMutation.isLoading;

  return (
    <div className="inline-flex items-center justify-center gap-0.5 text-inherit">
      <button
        type="button"
        className="p-1 rounded text-inherit hover:bg-gray-100 disabled:opacity-50"
        title="Download report PDF"
        aria-label="Download report PDF"
        disabled={busy}
        onClick={() => pdfMutation.mutate()}
      >
        {pdfMutation.isLoading ? (
          <InlineSpinner />
        ) : (
          <ArrowDownTrayIcon className="w-5 h-5" aria-hidden />
        )}
      </button>
      <button
        type="button"
        className="p-1 rounded text-inherit hover:bg-gray-100 disabled:opacity-50"
        title="Download consent form"
        aria-label="Download consent form"
        disabled={busy}
        onClick={() => consentMutation.mutate()}
      >
        {consentMutation.isLoading ? (
          <InlineSpinner />
        ) : (
          <DocumentCheckIcon className="w-5 h-5" aria-hidden />
        )}
      </button>
    </div>
  );
}
